/**
 * Platform Hub — top-level category hub for /platform.
 * Cross-references content/platform/*.md
 *
 * Lists all child platform routes:
 *  /platform/overview, /platform/capabilities, /platform/features, /platform/architecture,
 *  /platform/performance, /platform-security, /platform-dashboard, /platform/deployment,
 *  /platform/api, /platform/integrations, /platform/comparison, /platform/pricing,
 *  /platform/updates, /how-it-works
 */

export const platformHubMeta = {
  title: "Perception X2 Platform | AI-Powered Perception Amplification Stack",
  description:
    "The autonomous perception amplification platform: 9 proprietary AI engines, 50+ platform coverage, 144-dimensional emotional resonance. Explore architecture, performance, security, API, and integrations.",
  canonical: "https://perception.ac/platform",
  slug: "platform",
};

export interface PlatformChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const platformChildren: PlatformChild[] = [
  {
    code: "01",
    title: "Platform Overview",
    description:
      "The full landscape of Perception X2 — capabilities, scale, and impact across industries and audiences.",
    href: "/platform/overview",
    icon: "LayoutGrid",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Capabilities",
    description:
      "20 core capabilities that compose the perception amplification stack — from narrative engineering to crisis response.",
    href: "/platform/capabilities",
    icon: "Sparkles",
    accent: "purple",
  },
  {
    code: "03",
    title: "Features",
    description:
      "Granular feature inventory: 9 engines, 50+ integrations, 144 dimensions of resonance, real-time monitoring.",
    href: "/platform/features",
    icon: "Layers",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Architecture",
    description:
      "System architecture: multi-layer data fabric, engine orchestration, persona networks, and analytics mesh.",
    href: "/platform/architecture",
    icon: "Cpu",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Performance",
    description:
      "Speed, scale, and reliability benchmarks — sub-hour deployment, 99.9999% uptime, machine-speed response.",
    href: "/platform/performance",
    icon: "Gauge",
    accent: "amber",
  },
  {
    code: "06",
    title: "Security",
    description:
      "AES-256, zero trust, SOC 2 Type II, ISO 27001, defense-grade protection. Zero incidents in 15+ years.",
    href: "/platform-security",
    icon: "ShieldCheck",
    accent: "denim",
  },
  {
    code: "07",
    title: "Dashboard",
    description:
      "Real-time perception analytics: 144-dimensional heatmaps, sentiment timelines, competitive positioning.",
    href: "/platform-dashboard",
    icon: "BarChart3",
    accent: "violet",
  },
  {
    code: "08",
    title: "Deployment",
    description:
      "Four deployment tiers — Stealth, Moderate, Aggressive, Maximum — calibrated to scope and sensitivity.",
    href: "/platform/deployment",
    icon: "Rocket",
    accent: "rose",
  },
  {
    code: "09",
    title: "API",
    description:
      "RESTful API surface, webhooks, and SDKs. Integrate Perception X2 with your existing stack.",
    href: "/platform/api",
    icon: "Code2",
    accent: "cyan",
  },
  {
    code: "10",
    title: "Integrations",
    description:
      "Native connectors for CRM, marketing automation, analytics, and bespoke enterprise systems.",
    href: "/platform/integrations",
    icon: "Plug",
    accent: "emerald",
  },
  {
    code: "11",
    title: "Comparison",
    description:
      "Perception X2 vs. traditional PR, reputation agencies, and point-tool stacks. Side-by-side benchmarks.",
    href: "/platform/comparison",
    icon: "GitCompareArrows",
    accent: "amber",
  },
  {
    code: "12",
    title: "Pricing",
    description:
      "Engagement models calibrated to scale, scope, and strategic depth. Transparent tiers, no hidden costs.",
    href: "/platform/pricing",
    icon: "CircleDollarSign",
    accent: "purple",
  },
  {
    code: "13",
    title: "Updates & Roadmap",
    description:
      "Quarterly release cadence, capability expansion, and long-term platform evolution.",
    href: "/platform/updates",
    icon: "History",
    accent: "crimson",
  },
];

export interface PlatformStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const platformStats: PlatformStat[] = [
  { value: "9", label: "Proprietary Engines", to: 9, icon: "Cpu", accent: "crimson" },
  { value: "50+", label: "Platform Coverage", to: 50, suffix: "+", icon: "Globe", accent: "purple" },
  { value: "144", label: "Resonance Dimensions", to: 144, icon: "Activity", accent: "cyan" },
  { value: "1M+", label: "Max Personas", to: 1, suffix: "M+", decimals: 0, icon: "Users", accent: "emerald" },
  { value: "99.9999%", label: "Platform Uptime", to: 99.9999, decimals: 4, suffix: "%", icon: "ShieldCheck", accent: "amber" },
];

export interface PlatformFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const platformFeatures: PlatformFeature[] = [
  {
    title: "Multi-Layer Data Fabric",
    description: "Continuous ingestion across 50+ surfaces — social, search, news, forums, reviews, video, podcasts, and emerging digital surfaces.",
    icon: "Database",
    accent: "crimson",
  },
  {
    title: "Engine Orchestration",
    description: "Nine proprietary AI engines composed at runtime to match the strategic posture: stealth, moderate, aggressive, or maximum.",
    icon: "Workflow",
    accent: "purple",
  },
  {
    title: "144-Dimensional Resonance",
    description: "Proprietary analytical mesh — far beyond the 5–10 dimensions used by conventional platforms — for precision targeting.",
    icon: "Activity",
    accent: "cyan",
  },
  {
    title: "Adaptive Persona Networks",
    description: "10K to 1M+ simulated personas, each calibrated to cultural, demographic, and psychographic nuance.",
    icon: "Users",
    accent: "emerald",
  },
  {
    title: "Sub-Hour Deployment",
    description: "From strategic sign-off to live operations in under 60 minutes. Crisis-class response windows.",
    icon: "Zap",
    accent: "amber",
  },
  {
    title: "Zero Trust Security",
    description: "AES-256, TLS 1.3, zero trust architecture, SOC 2 Type II, ISO 27001. Defense-grade by default.",
    icon: "ShieldCheck",
    accent: "denim",
  },
  {
    title: "Native API & SDKs",
    description: "RESTful API, webhooks, and SDKs in TypeScript, Python, and Go. Wire Perception X2 into any stack.",
    icon: "Code2",
    accent: "violet",
  },
  {
    title: "Real-Time Dashboards",
    description: "Perception analytics, sentiment timelines, competitive positioning, and 144-D heatmaps — all live.",
    icon: "BarChart3",
    accent: "rose",
  },
];

export interface PlatformUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const platformUseCases: PlatformUseCase[] = [
  {
    title: "Enterprise Brand Defense",
    body: "Fortune 500 reputation hardening with continuous monitoring and autonomous counter-narrative deployment.",
    icon: "Shield",
    accent: "crimson",
  },
  {
    title: "Sovereign Narrative Operations",
    body: "National-scale perception engineering for governments, diplomatic missions, and public institutions.",
    icon: "Landmark",
    accent: "purple",
  },
  {
    title: "Crisis Containment",
    body: "Pre-built playbooks for category-killing events — operational in under 60 minutes from signal to response.",
    icon: "Siren",
    accent: "amber",
  },
  {
    title: "Competitive Perception",
    body: "Map and pre-empt competitor narratives. Engineer advantage before the market notices the move.",
    icon: "Swords",
    accent: "emerald",
  },
  {
    title: "Public Figure Engineering",
    body: "Personal brand protection and amplification for executives, public figures, and high-net-worth individuals.",
    icon: "Crown",
    accent: "cyan",
  },
  {
    title: "Market Launch Amplification",
    body: "New product and category launches with engineered perception dominance from day zero.",
    icon: "Rocket",
    accent: "violet",
  },
];

export interface PlatformWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const platformWhy: PlatformWhy[] = [
  {
    title: "Nine Proprietary Engines",
    body: "Not licensed, not white-labeled. Built in-house over 15+ years and tested in the world's most demanding environments.",
    metric: "9",
    accent: "crimson",
  },
  {
    title: "144-Dimensional Analysis",
    body: "Analytical granularity 14× deeper than the industry norm. Precision targeting at computational scale.",
    metric: "144D",
    accent: "purple",
  },
  {
    title: "Sub-Hour Operational",
    body: "Strategic sign-off to live deployment in under 60 minutes. The fastest operational cycle in perception management.",
    metric: "<60m",
    accent: "cyan",
  },
  {
    title: "95–98% Authenticity",
    body: "Content statistically indistinguishable from organic engagement. No detection. No suspicion. Pure authenticity.",
    metric: "98%",
    accent: "emerald",
  },
  {
    title: "Zero Security Incidents",
    body: "15+ years of global operations without a single breach. Defense and intelligence-grade protection.",
    metric: "0",
    accent: "amber",
  },
  {
    title: "300+ Elite Clients",
    body: "Governments, Fortune 500 enterprises, and global institutions trust Perception X2 with their strategic perception.",
    metric: "300+",
    accent: "denim",
  },
];

export interface PlatformFaq {
  q: string;
  a: string;
}

export const platformFaq: PlatformFaq[] = [
  {
    q: "What is the Perception X2 platform?",
    a: "Perception X2 is an autonomous perception amplification platform built on nine proprietary AI engines. It continuously monitors 50+ digital surfaces, analyzes perception across 144 emotional dimensions, engineers and amplifies narratives with 95–98% organic authenticity, and delivers 300–500% improvement in narrative penetration over organic baseline.",
  },
  {
    q: "How is Perception X2 different from traditional reputation management?",
    a: "Traditional reputation management is reactive — it monitors, reports, and responds, always a step behind. Perception X2 is proactive and autonomous. It identifies emerging threats before they crystallize, deploys counter-narratives in minutes rather than days, and engineers perception rather than simply tracking it.",
  },
  {
    q: "What does the platform actually run on?",
    a: "Perception X2 runs on a multi-layer data fabric with nine proprietary AI engines: Organic Simulation, Narrative Generation, Cognitive Resonance, Echo Chamber Architecture, Operations, Data Architecture, Persona Networks, Adaptive Response, and Content Factory. Together they form the autonomous perception amplification stack.",
  },
  {
    q: "How fast can Perception X2 be deployed?",
    a: "Standard engagements reach operational status in 4–6 weeks. Crisis-class deployments can be live in under 60 minutes from strategic sign-off. The platform is engineered for sub-hour operational cycles across all use cases.",
  },
  {
    q: "Is Perception X2 secure?",
    a: "Security is foundational, not optional. We maintain zero security incidents in 15+ years through end-to-end AES-256 encryption, TLS 1.3, zero trust architecture, SOC 2 Type II compliance, and ISO 27001 certification. The same standards used by defense and intelligence agencies protect every Perception X2 deployment.",
  },
  {
    q: "What platforms does Perception X2 cover?",
    a: "Perception X2 monitors and amplifies across 50+ digital platforms including social media (Twitter/X, LinkedIn, Instagram, TikTok, Facebook, Reddit, etc.), search engines (Google, Bing, DuckDuckGo), news outlets, forums, review sites, podcasts, video platforms, and emerging digital surfaces.",
  },
  {
    q: "How does the API work?",
    a: "Perception X2 exposes a RESTful API with webhooks for real-time events. SDKs are available in TypeScript, Python, and Go. The API surface covers monitoring, narrative deployment, analytics retrieval, persona orchestration, and crisis escalation.",
  },
  {
    q: "Can Perception X2 integrate with our existing tools?",
    a: "Yes. We offer native integrations for major CRM (Salesforce, HubSpot), marketing automation (Marketo, Pardot), analytics (Tableau, Looker), and bespoke enterprise systems. Our engineering team develops custom integrations for enterprise clients.",
  },
  {
    q: "What deployment tiers are available?",
    a: "Four tiers scale from 100 to 1,000,000+ personas: Stealth (100–1,000) for individual protection, Moderate (1,000–10,000) for SMB reputation, Aggressive (10,000–100,000) for enterprise campaigns, and Maximum (100,000–1,000,000+) for sovereign operations.",
  },
  {
    q: "How is success measured?",
    a: "We track narrative penetration (300–500% over baseline), organic authenticity rating (95–98%), sentiment shifts, share of voice, competitive positioning, and custom KPIs defined per engagement. All metrics are accessible through real-time dashboards.",
  },
];

export const platformCta = {
  eyebrow: "Begin Your Engagement",
  title: "The platform that defines perception engineering.",
  description:
    "Perception X2 is not a tool — it is a platform built for organizations where narrative determines outcome. Explore the architecture, integrate via API, or engage directly.",
  primary: { label: "Request a Platform Demo", href: "/contact" },
  secondary: { label: "View How It Works", href: "/how-it-works" },
  trustItems: [
    "9 proprietary engines",
    "50+ platforms",
    "144-D resonance",
    "Sub-hour deployment",
    "Zero-trust security",
  ],
};

/* Architecture radar — bespoke section data */
export interface PlatformRadarAxis {
  axis: string;
  value: number;
}

export const platformRadar: { label: string; axes: PlatformRadarAxis[]; accent: string }[] = [
  {
    label: "Perception X2",
    accent: "var(--color-crimson)",
    axes: [
      { axis: "Coverage", value: 96 },
      { axis: "Authenticity", value: 97 },
      { axis: "Speed", value: 94 },
      { axis: "Insight", value: 99 },
      { axis: "Resilience", value: 95 },
    ],
  },
];

/* Comparison matrix — bespoke section data */
export interface PlatformComparisonRow {
  dimension: string;
  perception: string;
  trad: string;
  point: string;
}

export const platformComparison: PlatformComparisonRow[] = [
  { dimension: "Operational model", perception: "Autonomous, AI-orchestrated", trad: "Manual, human-staffed", point: "API-driven dashboards" },
  { dimension: "Platform coverage", perception: "50+ digital surfaces", trad: "5–10 social channels", point: "Limited API connectors" },
  { dimension: "Analysis depth", perception: "144 emotional dimensions", trad: "5–10 sentiment classes", point: "Keyword sentiment only" },
  { dimension: "Response time", perception: "Sub-hour deployment", trad: "Days to weeks", point: "Manual ticket queue" },
  { dimension: "Persona scale", perception: "100 – 1,000,000+", trad: "Static lists, low hundreds", point: "Single-channel scripts" },
  { dimension: "Crisis readiness", perception: "Autonomous playbooks", trad: "On-call retainer", point: "No pre-built response" },
  { dimension: "Security", perception: "Zero-trust, defense-grade", trad: "Standard cloud", point: "Shared credentials" },
  { dimension: "Deployment cycle", perception: "<60 minutes operational", trad: "Weeks to months", point: "Quarterly releases" },
];
